import { useEffect, useRef, useState } from "react"
import ThemeModal from "components/Common/ThemeModal"
import { Button, Form } from "reactstrap"
import {
  createEmptyLoanErrors,
  LoanFormatPreview,
  LoanInformationFormFields,
  LoanInformationMetrics,
  LoanInformationSummary,
  normalizeLoanPayload,
  validateLoanFormValues,
} from "./MoneyReceiptLoanShared"
import { useAccountBankAccounts } from "hooks/useAccountBankAccount"
import { useAccountPaymentMethods } from "hooks/useAccountPaymentMethod"
import { useEmployees } from "hooks/employeeHook"

const MoneyReceiptLoanAddEdit = ({
  modal,
  toggleModal,
  moneyReceipt,
  initialValues,
  onSubmit,
  isSubmitting = false,
}) => {
  const [values, setValues] = useState(initialValues)
  const [errors, setErrors] = useState(createEmptyLoanErrors())

  const { data: bankAccountsResponse } = useAccountBankAccounts(
    { all: true, is_active: true },
    modal
  )
  const bankAccounts =
    bankAccountsResponse?.data?.data ?? bankAccountsResponse?.data ?? []

  const { data: paymentMethodsResponse } = useAccountPaymentMethods(
    { all: true, per_page: 200 },
    modal
  )
  const paymentMethods =
    paymentMethodsResponse?.data?.data ?? paymentMethodsResponse?.data ?? []

  const { data: employeesResponse } = useEmployees(
    { all: true, per_page: 500 },
    modal
  )
  const employees =
    employeesResponse?.data?.data ?? employeesResponse?.data ?? []

  const initialValuesRef = useRef(initialValues)
  initialValuesRef.current = initialValues

  useEffect(() => {
    if (modal) {
      setValues(initialValuesRef.current)
      setErrors(createEmptyLoanErrors())
    }
  }, [modal])

  useEffect(() => {
    const bankAccountId = values?.bank_account_id

    if (!bankAccountId) {
      setValues(current => {
        if (
          !current.payorder_account_name &&
          !current.payorder_account_number &&
          !current.payorder_routing_number &&
          !current.payorder_bank_name &&
          !current.payorder_branch_name
        ) {
          return current
        }

        return {
          ...current,
          payorder_account_name: "",
          payorder_account_number: "",
          payorder_routing_number: "",
          payorder_bank_name: "",
          payorder_branch_name: "",
        }
      })
      return
    }

    if (!bankAccounts.length) return

    const selectedAccount = bankAccounts.find(
      account => account.id === Number(bankAccountId)
    )
    if (!selectedAccount) return

    setValues(current => {
      if (
        current.payorder_account_name &&
        current.payorder_account_name !== selectedAccount.account_name
      ) {
        return current
      }

      return {
        ...current,
        payorder_account_name:
          selectedAccount.account_name || current.payorder_account_name || "",
        payorder_account_number:
          selectedAccount.account_number || current.payorder_account_number || "",
        payorder_routing_number:
          selectedAccount.routing_number || current.payorder_routing_number || "",
        payorder_bank_name:
          selectedAccount.bank_name || current.payorder_bank_name || "",
        payorder_branch_name:
          selectedAccount.branch_name || current.payorder_branch_name || "",
      }
    })
  }, [values?.bank_account_id, bankAccounts])

  const handleChange = event => {
    const { name, value } = event.target
    setValues(current => ({
      ...current,
      [name]: value,
    }))
    setErrors(current => ({
      ...current,
      [name]: "",
    }))
  }

  const handleReset = () => {
    setValues(initialValues)
    setErrors(createEmptyLoanErrors())
  }

  const handleSubmit = async event => {
    event.preventDefault()

    if (isSubmitting) {
      return
    }

    const nextErrors = validateLoanFormValues(values)
    setErrors(nextErrors)

    if (Object.values(nextErrors).some(Boolean)) {
      return
    }

    const payload = normalizeLoanPayload(values)

    try {
      await onSubmit(payload)
      toggleModal()
    } catch (error) {
      // keep modal open so user can retry; parent shows toast
    }
  }

  const title = moneyReceipt?.mr_no
    ? `Edit Loan Information - ${moneyReceipt.mr_no}`
    : "Edit Loan Information"

  const handleToggle = () => {
    if (isSubmitting) {
      return
    }
    toggleModal()
  }

  return (
    <ThemeModal
      isOpen={modal}
      toggle={handleToggle}
      title={title}
      size="lg"
      modalBodyClass="bg-body-tertiary"
      footer={
        <div className="d-flex justify-content-end gap-2 w-100">
          <Button color="light" onClick={handleReset} disabled={isSubmitting}>
            Reset
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      }
    >
      <Form onSubmit={handleSubmit}>
        <LoanInformationSummary loanInformation={values} />
        <div className="rounded-3 border bg-white p-3 mb-4">
          <LoanInformationFormFields
            values={values}
            errors={errors}
            onChange={handleChange}
            disabled={isSubmitting}
            bankAccounts={bankAccounts}
            paymentMethods={paymentMethods}
            employees={employees}
          />
        </div>
        <LoanFormatPreview loanInformation={values} className="mb-4" />
        <LoanInformationMetrics loanInformation={values} />
      </Form>
    </ThemeModal>
  )
}

export default MoneyReceiptLoanAddEdit
